import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://a2insure.com/api/uploads/10cf2a29a027490e8559d1f36cc40701.svg";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/products", label: "Products" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
    { to: "/appointments", label: "Schedule" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav data-testid="main-navbar" className="sticky top-0 z-50 bg-[#002147] border-b border-[#0F3460]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 shrink-0">
            <img
              src={LOGO_URL}
              alt="Anderson & Anderson Insurance LLC"
              className="h-10 lg:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-1">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(link.to)
                    ? "text-[#D4AF37] bg-white/5"
                    : "text-white/80 hover:text-white hover:bg-white/5"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA + Phone */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:2197797040" className="flex items-center gap-2 text-white/80 hover:text-white text-sm transition-colors">
              <Phone className="h-4 w-4 text-[#D4AF37]" />
              (219) 779-7040
            </a>
            <Link to="/contact">
              <Button
                data-testid="navbar-quote-btn"
                className="bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold px-6 rounded-md shadow-md"
              >
                Get a Quote
              </Button>
            </Link>
            <Link to="/admin" data-testid="navbar-admin-link" className="p-2 rounded-md text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors" title="Admin">
              <Lock className="h-4 w-4" />
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#002147] border-t border-[#0F3460] animate-fade-in">
          <div className="px-4 py-4 space-y-1">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                onClick={() => setMobileOpen(false)}
                className={`block px-4 py-3 text-sm font-medium rounded-md ${
                  isActive(link.to)
                    ? "text-[#D4AF37] bg-white/5"
                    : "text-white/80 hover:text-white hover:bg-white/5"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-[#0F3460]">
              <a href="tel:2197797040" className="flex items-center gap-2 text-white/80 px-4 py-2 text-sm">
                <Phone className="h-4 w-4 text-[#D4AF37]" />
                (219) 779-7040
              </a>
              <Link to="/contact" onClick={() => setMobileOpen(false)}>
                <Button className="w-full mt-3 bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold">
                  Get a Quote
                </Button>
              </Link>
              <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-white/40 px-4 py-2 mt-2 text-sm">
                <Lock className="h-3.5 w-3.5" /> Admin Login
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
